/** Old hardcoded catalog — used only to migrate saved "all 10" job prefs. */
export const LEGACY_DISCIPLINE_CATALOG_IDS = [
  'piping', 'hvac', 'structural', 'mechanical', 'ducts',
  'boilermaker', 'vessels', 'civil', 'electrical', 'indirect'
];

/**
 * Keep the user's Discipline picks when the live P6 list changes.
 * If they had every named value on and a second XER adds (no value),
 * include it — otherwise that file's hours vanish from the histogram.
 */
export function migrateDisciplineSelection(prev, liveIds) {
  if (!liveIds?.length) return [];
  if (!prev?.length) return liveIds;
  const liveSet = new Set(liveIds);
  const keep = prev.filter((id) => liveSet.has(id));
  const isOldAll = prev.length === LEGACY_DISCIPLINE_CATALOG_IDS.length
    && LEGACY_DISCIPLINE_CATALOG_IDS.every((id) => prev.includes(id));
  if (isOldAll) return liveIds;
  if (!keep.length) return liveIds;
  const namedLive = liveIds.filter((id) => id && id !== '__none__');
  const hadAllNamed = namedLive.length > 0 && namedLive.every((id) => prev.includes(id));
  const noneAppeared = liveIds.includes('__none__') && !prev.includes('__none__');
  if (hadAllNamed && noneAppeared) {
    return liveIds.filter((id) => keep.includes(id) || id === '__none__');
  }
  return keep;
}

/** Stable sorted P6 proj_id list from a comma string or array. */
export function normalizeProjIdList(ids) {
  if (ids == null || ids === '') return [];
  const raw = Array.isArray(ids) ? ids : String(ids).split(',');
  return [...new Set(raw.map((id) => String(id || '').trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

export function idsFromCurveProject(project, consolidated) {
  if (Array.isArray(consolidated) && consolidated.length) {
    return normalizeProjIdList(consolidated.map((p) => p?.projId));
  }
  if (project?.kind === 'consolidated' && project.projId != null && project.projId !== '') {
    return normalizeProjIdList(String(project.projId).split(','));
  }
  if (project?.projId != null && project.projId !== '') {
    return normalizeProjIdList([project.projId]);
  }
  return [];
}

/**
 * Last-good cache is served with meta.refreshing while P6 rebuilds.
 * Re-applying it after a live curve is on screen flashes stale hours
 * (26-LG-0174: 70,505 last-good ↔ 70,513 live).
 */
export function shouldReplaceHistogramPayload(payload, { hasPainted } = {}) {
  if (!payload) return false;
  if (payload.meta?.refreshing && hasPainted) return false;
  return true;
}

/**
 * /projects is the live picker list. Analytics last-good must not overwrite
 * remainHours already on screen — that is the 70,513 ↔ 70,505 blink.
 */
export function mergeP6ProjectLists(prev, incoming) {
  if (!incoming?.length) return prev || [];
  if (!prev?.length) return incoming;
  const byId = new Map(prev.map((p) => [String(p.projId), p]));
  incoming.forEach((p) => {
    const id = String(p.projId);
    if (!byId.has(id)) byId.set(id, p);
  });
  return [...byId.values()];
}

/**
 * True when the histogram payload is the XERs the pickers asked for.
 * Auto (empty pick) always matches. A last-good cache from a different
 * checkbox set must not paint — that is how "2 XERs consolidated" showed
 * one file's hours.
 */
export function liveMatchesXerSelection(live, weeklyIds, proposalIds) {
  const wantW = normalizeProjIdList(weeklyIds);
  const wantP = normalizeProjIdList(proposalIds);
  if (!wantW.length && !wantP.length) return true;
  const gotW = idsFromCurveProject(
    live?.remaining?.project || live?.meta?.weeklyScheduleUpdate,
    live?.remaining?.consolidatedFrom || live?.meta?.consolidatedFrom
  );
  const gotP = idsFromCurveProject(
    live?.proposal?.project || live?.meta?.proposalProject,
    live?.proposal?.consolidatedFrom || live?.meta?.proposalConsolidatedFrom
  );
  const same = (want, got) => {
    if (!want.length) return true;
    if (!got.length) return false;
    return want.join('\u241f') === got.join('\u241f');
  };
  return same(wantW, gotW) && same(wantP, gotP);
}
